import { Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { useTranslation } from "react-i18next";
import CopyTaskIdButton from "@/components/backgroundTasks/copyTaskIdButton";
import TaskCompanyList from "@/components/backgroundTasks/taskCompanyList";
import SummaryStatTiles from "@/components/summaryCard/summaryStatTiles";
import SummaryIssueList from "@/components/summaryCard/summaryIssueList";
import cardStyles from "@/components/summaryCard/summaryCard.module.css";
import styles from "@/components/backgroundTasks/batchUploadTaskResult.module.css";

interface FailedUpload {
  file_name?: string;
  subject_id?: string;
  subject_name?: string;
  error?: string;
}

export interface BatchUploadTaskData {
  target?: {
    kwargs?: {
      file_paths?: { original_filename?: string }[];
      subject_info_set?: [string, string][];
    };
  };
  result?: {
    total_successful_uploads_count?: number;
    total_failed_uploads_count?: number;
    failed_uploads?: FailedUpload[];
    companies?: string[];
    folder_name?: string | null;
  } | null;
}

interface Props {
  taskId: string;
  task?: BatchUploadTaskData | null;
}

export default function BatchUploadTaskResult({ taskId, task }: Props) {
  const { t } = useTranslation();
  const fileNames = (task?.target?.kwargs?.file_paths ?? [])
    .map((filePath) => filePath.original_filename)
    .filter(Boolean);
  const companySubjectIdsFromTarget = (
    task?.target?.kwargs?.subject_info_set ?? []
  ).map(([, subjectId]) => subjectId);
  const companies = task?.result?.companies ?? companySubjectIdsFromTarget;
  const folderName = task?.result?.folder_name;
  const successfulUploadCount =
    task?.result?.total_successful_uploads_count ?? 0;
  const failedUploadCount = task?.result?.total_failed_uploads_count ?? 0;
  const failedUploads = task?.result?.failed_uploads ?? [];

  return (
    <section className={cardStyles.card}>
      <div className={cardStyles.header}>
        <span className={cardStyles.iconContainer} aria-hidden="true">
          <CloudUploadOutlinedIcon />
        </span>
        <div className={cardStyles.headerCopy}>
          <Typography className={cardStyles.headerLabel}>
            {t("files")}
          </Typography>
          <Typography className={cardStyles.headerValue}>
            {fileNames.length > 0 ? fileNames.join(", ") : "-"}
          </Typography>
          {folderName && (
            <Typography className={styles.folderInfo}>
              <FolderOutlinedIcon
                className={styles.folderIcon}
                aria-hidden="true"
              />
              {t("folder")}: {folderName}
            </Typography>
          )}
        </div>
        <CopyTaskIdButton taskId={taskId} />
      </div>

      <SummaryStatTiles
        stats={[
          { label: t("successful_uploads"), value: successfulUploadCount },
          {
            label: t("failed_uploads"),
            value: failedUploadCount,
            isFailureCount: true,
          },
        ]}
      />

      <SummaryIssueList
        issues={failedUploads.map((failedUpload, index) => ({
          key: `${failedUpload.subject_id}-${failedUpload.file_name}-${index}`,
          title: `${failedUpload.subject_name ?? failedUpload.subject_id} · ${
            failedUpload.file_name
          }`,
          details: failedUpload.error ? [failedUpload.error] : [],
        }))}
      />

      <TaskCompanyList companies={companies} />
    </section>
  );
}
